// /BlogData.js
// Categories, tags, authors
export const CATEGORIES = [
  { key: "product", label: "Product Updates" },
  { key: "tutorials", label: "Tutorials & How-to" },
  { key: "insights", label: "Market Insights" },
  { key: "security", label: "Security" },
  { key: "compliance", label: "Compliance" },
  { key: "dev", label: "API & Dev Corner" },
];

export const TAGS = [
  "Spot Trading",
  "Futures",
  "AI Trading",
  "KYC",
  "Funding Rate",
  "Liquidity",
  "PnL",
  "Market Structure",
  "Order Types",
  "Risk",
  "SDK",
  "WebSocket",
];

export const AUTHORS = {
  editorial:  { name: "1CryptoX Editorial", role: "Editorial Team",          avatar: "" },
  research:   { name: "Research Desk",      role: "Markets Research",         avatar: "" },
  academy:    { name: "Academy Team",       role: "Education",                avatar: "" },
  secops:     { name: "Security Ops",       role: "Security Team",            avatar: "" },
  compliance: { name: "Compliance Office",  role: "Compliance",               avatar: "" },
  devrel:     { name: "Dev Relations",      role: "Developer Relations",      avatar: "" },
};

// Posts with real web images (hero + inline)
export const POSTS = [
  {
    id: "advanced-order-types",
    title: "Introducing Advanced Order Types for Spot Trading",
    category: "product",
    tags: ["Spot Trading", "Order Types", "Liquidity"],
    author: AUTHORS.editorial,
    date: "2025-11-01",
    readMins: 10,
    // Hero: macro tech visual (free stock)
    hero: "https://images.pexels.com/photos/270549/pexels-photo-270549.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "We’re rolling out a disciplined suite of order types designed for precision execution and auditability—Post-Only, IOC, FOK, and refined Stop logic with exchange-level guards.",
    featured: true,
    trendScore: 97,
    tldr: [
      "Post-Only ensures maker-fee eligibility and avoids immediate taker execution.",
      "IOC and FOK give you strict control in micro-volatile regimes.",
      "Stop logic was refined to reduce whipsaw triggers using exchange-level guards.",
    ],
    takeaways: [
      "Better intent expression → cleaner audit trails.",
      "Reduced accidental taker fills on Post-Only.",
      "Improved stop-event handling in volatility spikes.",
    ],
    body: [
      { type: "p", text: "Execution quality starts with precise intent. This release improves how traders express and audit their orders across high-velocity markets." },
      // Inline image
      {
        type: "img",
        src: "https://images.pexels.com/photos/270549/pexels-photo-270549.jpeg?cs=srgb&fm=jpg",
        alt: "Abstract macro gold circuitry",
        caption: "Precision mindset: price levels, triggers, and routing.",
      },
      { type: "h2", text: "Post-Only: Intent without Taker Leakage" },
      { type: "p", text: "Post-Only prevents immediate taker execution and maintains maker-fee eligibility. If your limit price would cross the book, the order is canceled rather than filled as taker." },
      // Second inline image
      {
        type: "img",
        src: "https://images.pexels.com/photos/373543/pexels-photo-373543.jpeg?cs=srgb&fm=jpg",
        alt: "Stylized market depth and liquidity",
        caption: "Maker queue positioning vs. taker cross (conceptual).",
      },
      { type: "h2", text: "IOC & FOK: Deterministic Control" },
      { type: "p", text: "Immediate-or-Cancel executes what’s available instantly and cancels the rest; Fill-or-Kill demands complete fulfillment or nothing. In both cases, your exposure is predictable in micro-bursts." },
      { type: "h2", text: "Stop Logic with Exchange-Level Guards" },
      { type: "p", text: "We’ve refined stop triggers using mark-price and volatility-aware guards. The goal: fewer false triggers during shallow wicks, while preserving protection during genuine dislocations." },
      { type: "callout", variant: "info", title: "Note", text: "Enhanced telemetry exposes fill narratives, queue position snapshots, and trigger provenance for compliance reviews." },
      { type: "disclosure", text: "Not investment advice. Trading involves risk, including the possible loss of principal." },
    ],
  },

  {
    id: "futures-funding-guide",
    title: "Futures Funding: A Practical Guide to Rates, Windows, and Edge",
    category: "insights",
    tags: ["Futures", "Funding Rate", "PnL", "Risk"],
    author: AUTHORS.research,
    date: "2025-10-27",
    readMins: 12,
    // Hero: dark financial chart
    hero: "https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "Funding ties perpetuals to spot. We break down regimes, windows, and risk controls traders use to avoid crowding while extracting disciplined edge.",
    featured: true,
    trendScore: 90,
    tldr: [
      "Premium → longs pay shorts; discount → shorts pay longs.",
      "Edge ≠ single signal. Pair funding with liquidity shelves and basis behavior.",
      "Guardrails: regime detection, position caps, and kill-switch logic.",
    ],
    takeaways: [
      "Avoid crowding near obvious windows.",
      "Validate scenarios with sharp basis swings.",
      "Track mark-price methodology to avoid false assumptions.",
    ],
    body: [
      { type: "p", text: "Perpetual contracts drift toward spot via funding. Over-rotation on rate snapshots can be expensive; discipline and guardrails matter more than forecasts." },
      {
        type: "img",
        src: "https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?cs=srgb&fm=jpg",
        alt: "Abstract dark chart lines",
        caption: "Funding & basis dance around the index price.",
      },
      { type: "h2", text: "How Funding Forms" },
      { type: "p", text: "When perp trades at a premium, longs pay to shorts, and vice versa in discount regimes. But the microstructure—queue priority, imbalance, and liquidity shelves—shapes realized outcomes." },
      { type: "h2", text: "Windows, Crowding, and Slippage" },
      { type: "p", text: "Edge erodes when everyone crowds the same window. Smooth entries, soft exits, and caps per regime reduce slippage and tail risk." },
      {
        type: "img",
        src: "https://images.pexels.com/photos/7567431/pexels-photo-7567431.jpeg?cs=srgb&fm=jpg",
        alt: "Heatmap style market view",
        caption: "Market depth heatmap around funding windows (concept).",
      },
      { type: "callout", variant: "warning", title: "Risk", text: "A compressed basis after a volatility shock can invert your thesis. Keep position sizing adaptive." },
      { type: "disclosure", text: "Not investment advice. Derivatives carry significant risk and may not be suitable for all investors." },
    ],
  },

  {
    id: "from-zero-to-trade",
    title: "From Zero to Trade: A Step-by-Step Tutorial for New Users",
    category: "tutorials",
    tags: ["Spot Trading", "Order Types", "Risk"],
    author: AUTHORS.academy,
    date: "2025-11-03",
    readMins: 14,
    // Hero: onboarding style
    hero: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "A pragmatic, safety-first walkthrough: secure your account, choose order types, size positions, and validate triggers before you scale.",
    featured: true,
    trendScore: 88,
    tldr: [
      "Secure first: 2FA, allowlists, device hygiene.",
      "Learn the building blocks: Market, Limit, Stop.",
      "Start small; audit executions; codify your checklist.",
    ],
    takeaways: [
      "Muscle memory via repeatable pre-trade routine.",
      "Defense-first with stops and size controls.",
      "Post-trade reviews close the learning loop.",
    ],
    body: [
      {
        type: "img",
        src: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?cs=srgb&fm=jpg",
        alt: "Onboarding checklist UI mock",
        caption: "A simple routine compounds into durable skill.",
      },
      { type: "h2", text: "Secure Your Account" },
      { type: "p", text: "Enable hardware-backed 2FA, use unique passwords, and configure withdrawal allowlists. Treat email as a high-risk vector." },
      { type: "h2", text: "Order Types that Matter" },
      { type: "p", text: "Market for immediacy, Limit for price control, Stop for defense. Practice with small notionals until execution reports match expectation." },
      { type: "h2", text: "A Repeatable Routine" },
      { type: "p", text: "Checklist: thesis → size → entry → protective stop → review. Consistency turns intent into measurable outcomes." },
      { type: "disclosure", text: "Educational content only. No investment advice." },
    ],
  },

  {
    id: "security-baseline",
    title: "Security Baseline: Keys, Devices, and Human Factors",
    category: "security",
    tags: ["Security", "Risk"],
    author: AUTHORS.secops,
    date: "2025-10-29",
    readMins: 9,
    // Hero: security phone + lock
    hero: "https://images.pexels.com/photos/11391947/pexels-photo-11391947.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "People, not tools, are the core. Keys, devices, escalation channels, and predictable drills harden your day-to-day.",
    featured: false,
    trendScore: 76,
    tldr: [
      "Hardware-backed keys and segmented privileges.",
      "Aggressive patching; isolate admin flows.",
      "Practice phishing drills with real escalation paths.",
    ],
    takeaways: [
      "Security posture is a habit system.",
      "Reduce blast radius via isolation.",
      "Train for failure before it arrives.",
    ],
    body: [
      { type: "p", text: "Adopt hardware tokens, rotate secrets, and separate admin devices. Your team should know exactly who to call and how to escalate under stress." },
      {
        type: "img",
        src: "https://images.pexels.com/photos/11391947/pexels-photo-11391947.jpeg?cs=srgb&fm=jpg",
        alt: "Phone secured with chain and lock",
        caption: "Hardware keys and device hygiene reduce attack paths.",
      },
      { type: "disclosure", text: "Security recommendations are provided as-is without warranties." },
    ],
  },

  {
    id: "compliance-in-motion",
    title: "Compliance in Motion: KYC, Sanctions, and Export Controls",
    category: "compliance",
    tags: ["Compliance", "KYC"],
    author: AUTHORS.compliance,
    date: "2025-10-25",
    readMins: 11,
    // Hero: abstract compliance flow
    hero: "https://images.pexels.com/photos/4778611/pexels-photo-4778611.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "Compliance is an active system. Identity verification, sanctions screening, and region-specific controls keep our ecosystem safe and reliable.",
    featured: false,
    trendScore: 82,
    tldr: [
      "KYC verifies participants; sanctions lists and ongoing scoring manage risk.",
      "Access may change with regulatory updates; we communicate early.",
      "Users should maintain current documents and read in-app notices.",
    ],
    takeaways: [
      "Documentation hygiene reduces friction.",
      "Transparent notices prevent surprises.",
      "Controls map to jurisdictional duties.",
    ],
    body: [
      { type: "p", text: "We calibrate controls to obligations by region. When rules change, we notify impacted users through in-app banners and email." },
      {
        type: "img",
        src: "https://images.pexels.com/photos/4778611/pexels-photo-4778611.jpeg?cs=srgb&fm=jpg",
        alt: "Compliance workflow abstract",
        caption: "KYC/AML flow with watchlist screening (concept).",
      },
      { type: "disclosure", text: "Compliance overview for information only; subject to change with regulation." },
    ],
  },

  {
    id: "ws-real-time-data",
    title: "Developers’ Corner: Real-Time Data over WebSocket",
    category: "dev",
    tags: ["WebSocket", "SDK", "Market Structure"],
    author: AUTHORS.devrel,
    date: "2025-10-31",
    readMins: 12,
    // Hero: network/switch connectivity
    hero: "https://images.pexels.com/photos/2881227/pexels-photo-2881227.jpeg?cs=srgb&fm=jpg",
    excerpt:
      "From subscriptions and throttling to order book diffs and snapshots—compose resilient dashboards without UI thrash.",
    featured: false,
    trendScore: 84,
    tldr: [
      "Single source of truth for pairs.",
      "Trades + depth deltas with debounce.",
      "Sequence verification + periodic snapshots.",
    ],
    takeaways: [
      "Back-off with jitter protects both sides.",
      "Memory caps prevent slow leaks.",
      "Telemetry enables post-incident audits.",
    ],
    body: [
      { type: "p", text: "Start with one canonical product list. Subscribe minimally, debounce UI updates to ~60–120ms, and verify sequence numbers before applying depth diffs." },
      {
        type: "img",
        src: "https://images.pexels.com/photos/2881227/pexels-photo-2881227.jpeg?cs=srgb&fm=jpg",
        alt: "Network switch ports close-up",
        caption: "Order book diff merge and snapshot recovery need reliable edges.",
      },
      { type: "disclosure", text: "Technical guidance only; performance depends on your infra and network conditions." },
    ],
  },
];
